// src/features/activity/MappingsTab.tsx
// Admin: Kafka topic → Activity mapping rules. Extend ActivityService with
// new event sources without shipping code.

import { useCallback, useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { activityActivityMappingStore } from '@core/stores';
import { Button, Badge, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty, ErrorAlert } from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { ActivityMappingConfig } from '@core/types/activity';
import { MappingFormDialog } from './MappingFormDialog';
import { categoryKey, scopeKey, errMessage } from './logic';

export const MappingsTab = observer(() => {
  const { t } = useI18n();
  const [items, setItems] = useState<ActivityMappingConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ActivityMappingConfig | null>(null);
  const [deleting, setDeleting] = useState<ActivityMappingConfig | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    activityActivityMappingStore.ListMappingConfigs()
      .then((res) => setItems(res.configs ?? []))
      .catch((e: unknown) => setError(errMessage(e, t('activity.settings.mappings.loadError'))))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    if (!deleting?.entity?.id) return;
    setBusy(true);
    try {
      await activityActivityMappingStore.DeleteMappingConfig(deleting.entity.id);
      setDeleting(null);
      load();
    } catch {
      setError(t('activity.settings.mappings.loadError'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-foreground">{t('activity.settings.mappings.title')}</h3>
          <p className="text-xs text-muted-foreground">{t('activity.settings.mappings.subtitle')}</p>
        </div>
        <Button size="sm" onClick={() => { setEditing(null); setFormOpen(true); }}>{t('activity.settings.mappings.add')}</Button>
      </div>

      {error && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}

      <Table hoverable>
        <TableHeader>
          <TableRow>
            <TableHead>{t('activity.settings.mappings.column.topic')}</TableHead>
            <TableHead>{t('activity.settings.mappings.column.eventType')}</TableHead>
            <TableHead>{t('activity.settings.mappings.column.verb')}</TableHead>
            <TableHead>{t('activity.settings.mappings.column.category')}</TableHead>
            <TableHead>{t('activity.settings.mappings.column.scope')}</TableHead>
            <TableHead>{t('activity.settings.mappings.column.enabled')}</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">{t('common.loading')}</TableCell></TableRow>
          ) : items.length === 0 ? (
            <TableEmpty colSpan={7} message={t('activity.settings.mappings.empty')} />
          ) : (
            items.map((m) => (
              <TableRow key={m.entity?.id}>
                <TableCell className="font-mono text-xs">{m.kafka_topic}</TableCell>
                <TableCell className="text-sm">{m.event_type}</TableCell>
                <TableCell className="text-sm">{m.verb}</TableCell>
                <TableCell className="text-sm">{t(`activity.category.${categoryKey(m.category)}`)}</TableCell>
                <TableCell className="text-sm">{t(`activity.scope.${scopeKey(m.default_scope)}`)}</TableCell>
                <TableCell>
                  <Badge variant={m.enabled ? 'success' : 'secondary'} size="sm">
                    {m.enabled ? t('common.enabled') : t('common.disabled')}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(m); setFormOpen(true); }}>{t('common.edit')}</Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeleting(m)}>{t('common.delete')}</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <MappingFormDialog open={formOpen} mapping={editing} onClose={() => setFormOpen(false)} onSaved={load} />
      <ConfirmDialog
        open={!!deleting}
        title={t('activity.settings.mappings.form.delete')}
        description={t('activity.settings.mappings.form.deleteConfirm')}
        destructive
        isConfirming={busy}
        onConfirm={remove}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
});

export default MappingsTab;
